import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { getMe, type Me } from "@/lib/api";
import { ThemeProvider } from "@/lib/theme";
import { ToastProvider } from "@/components/ui/toast";
import { ConfirmProvider } from "@/components/ui/confirm";
import { Login } from "@/views/Login";
import { TwoFactor } from "@/views/TwoFactor";
import { Layout } from "@/views/Layout";
import { Servers } from "@/views/Servers";
import { Secrets } from "@/views/Secrets";
import { Projects } from "@/views/Projects";
import { ProjectDetail, AgentSettingsPage } from "@/views/ProjectDetail";
import { ApprovalPage } from "@/views/ApprovalPage";
import { RuleEditor } from "@/views/create/RuleEditor";
import { CreateChannel } from "@/views/create/CreateChannel";
import { CreateAccessToken } from "@/views/create/CreateAccessToken";
import { CreateAgent } from "@/views/create/CreateAgent";
import { ConnectServer } from "@/views/create/ConnectServer";
import { Members } from "@/views/Members";
import { Credits } from "@/views/Credits";
import { Billing } from "@/views/Billing";
import { Profile } from "@/views/Profile";
import { Onboarding } from "@/views/Onboarding";

// Legacy route alias: the "Notifications" section was renamed to "Channels".
// Keep the old create-form URL working (the old /settings/notifications tab is
// aliased inside AgentSettingsPage).
function RedirectChannelsNew() {
  const { projectId } = useParams();
  return <Navigate to={`/agents/${projectId}/channels/new`} replace />;
}

// Toast + Confirm providers wrap everything so any view can surface
// errors/successes and gate destructive actions behind a confirmation.
export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <ConfirmProvider>
          <AppRoutes />
        </ConfirmProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

function AppRoutes() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = () =>
    getMe()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));

  useEffect(() => {
    void refresh();
  }, []);

  if (loading) {
    return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (!me) {
    // Unauthenticated shell. /totp is the second-factor step a first factor
    // redirects to (the cb_2fa challenge cookie is set, no session yet);
    // everything else is the sign-in screen.
    return (
      <Routes>
        <Route path="/totp" element={<TwoFactor onVerified={() => void refresh()} />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Hard paywall: a newly-created workspace ("pending") is unusable until it
  // subscribes. The status is present only for the CURRENT workspace. Existing
  // ("" grandfathered) and "active" workspaces fall through to the normal app.
  // /approve stays reachable (an external approver may not own this workspace).
  const current = me.workspaces?.find((w) => w.tenant_id === me.current_tenant_id);
  const paywalled = current?.status === "pending";

  // The approval page renders standalone (no app shell) — a minimal
  // consent-style surface a notification links to. A paywalled workspace
  // replaces the whole app shell with the Onboarding plan picker.
  return (
    <Routes>
      <Route path="/approve/:projectId/:ticket" element={<ApprovalPage />} />
      <Route
        path="*"
        element={
          paywalled ? (
            <Onboarding me={me} onActivated={() => void refresh()} onSignedOut={() => setMe(null)} />
          ) : (
            <Layout me={me} onSignedOut={() => setMe(null)}>
              <Routes>
                <Route path="/" element={<Navigate to="/agents" replace />} />
                <Route path="/servers" element={<Servers />} />
                <Route path="/servers/new" element={<ConnectServer />} />
                <Route path="/secrets" element={<Secrets />} />
                <Route path="/agents" element={<Projects />} />
                <Route path="/agents/new" element={<CreateAgent />} />
                <Route path="/agents/:projectId/policy/rules/new" element={<RuleEditor />} />
                <Route path="/agents/:projectId/policy/rules/:ruleId/edit" element={<RuleEditor />} />
                <Route path="/agents/:projectId/channels/new" element={<CreateChannel />} />
                <Route path="/agents/:projectId/notifications/new" element={<RedirectChannelsNew />} />
                <Route path="/agents/:projectId/access-tokens/new" element={<CreateAccessToken />} />
                <Route path="/agents/:projectId/settings/:section" element={<AgentSettingsPage />} />
                <Route path="/agents/:projectId" element={<ProjectDetail />} />
                <Route path="/members" element={<Members />} />
                <Route path="/credits" element={<Credits />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="*" element={<Navigate to="/agents" replace />} />
              </Routes>
            </Layout>
          )
        }
      />
    </Routes>
  );
}
